import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
  memo,
} from "react";
import AutocompleteElement from "../autocomplete";
import { fetchApi } from "../../../ancillaries/api";
import { debounce } from "@helpers/debounce";
import { EntityType } from "../../../ancillaries/types";
import { mapToOptionsFiltered } from "@helpers/map-to-options-filtered";
import { OptionMapper } from "@helpers/get-option-mapper";

interface AutocompletProps {
  label: string;
  multi: boolean;
  endpoint: string;
  initial?:
    | { id: number; label: string }
    | { id: number; label: string }[]
    | null;
  queryParam: any;
  updateFields: any;
  noOptionsText?: string;
  restricted?: boolean;
  size?: "small" | "medium";
  variant?: "standard" | "outlined" | "filled" | undefined;
  limit?: number;
  sx?: any;
  required?: boolean;
  filter?: number;
  extraParams?: { [key: string]: any } | { [key: string]: any }[];
  disabled?: boolean;
  className?: string;
  responseSize?: number;
  optionMapper?: OptionMapper;
  disabledFetch?: boolean;
  onSelectOption?: (
    option: Option | Option[] | null,
    rawItem?: any | any[] | null,
  ) => void;
  appliedValue?: any;
  valueKey?: string;
  initialLoadOnOpen?: boolean;
  error?: boolean;
  helperText?: string;
  none?: boolean;
  applyCleaning?: boolean;
}

interface Option {
  id: number | string;
  label: string;
}

const AutocompleteDynamic: React.FC<AutocompletProps> = ({
  label,
  multi,
  initial,
  queryParam,
  endpoint,
  updateFields,
  size,
  variant,
  limit,
  noOptionsText,
  sx,
  required,
  filter,
  extraParams,
  responseSize = 20,
  optionMapper,
  className,
  onSelectOption,
  appliedValue,
  valueKey,
  restricted = false,
  disabled = false,
  disabledFetch = false,
  initialLoadOnOpen = false,
  error = false,
  helperText,
  none = false,
  applyCleaning = false,
}) => {
  const isFirstRender = useRef(true);
  const hasFetchedRef = useRef(false);
  const [options, setOptions] = useState<Option[]>([]);
  const [inputValue, setInputValue] = useState("");
  const skipNextFetch = useRef(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    isFirstRender.current = false;
  }, []);

  const fetchOptions = useCallback(
    async (search: string, size: number) => {
      setOptions([]);
      try {
        setLoading(true);
        if (isInvalidExtraParams(extraParams)) {
          setOptions([]);
          return;
        }
        const response = await fetchApi<EntityType>(endpoint, {
          [queryParam]: search,
          page: 1,
          size,
          ...extraParams,
        });

        let mapped: Option[] = [];

        if (optionMapper) {
          mapped = mapToOptionsFiltered(
            { items: response.items },
            filter,
            optionMapper,
          );
        } else {
          mapped = mapToOptionsFiltered({ items: response.items }, filter);
        }

        setOptions(
          none ? [{ id: "__NONE__", label: "(Blanks)" }, ...mapped] : mapped,
        );
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [endpoint, queryParam, filter, extraParams, optionMapper],
  );
  const debouncedFetch = useMemo(
    () => debounce((s: string) => fetchOptions(s, responseSize), 300),
    [fetchOptions, responseSize, extraParams],
  );

  useEffect(() => {
    if (disabledFetch || appliedValue || initialLoadOnOpen) return;
    if (hasFetchedRef.current) return;
    if (!initial || (Array.isArray(initial) && initial.length === 0)) {
      hasFetchedRef.current = true;
      fetchOptions("", responseSize);
    }
  }, [
    filter,
    initial,
    extraParams,
    fetchOptions,
    responseSize,
    disabledFetch,
    appliedValue,
    initialLoadOnOpen,
  ]);

  useEffect(() => {
    if (skipNextFetch.current) {
      skipNextFetch.current = false;
      return;
    }
    if (inputValue.length >= 3) {
      const cleanValue = applyCleaning
        ? inputValue.replace(/^\([^)]*\)\s*/, "").trim()
        : inputValue.trim();
      debouncedFetch(cleanValue);
    }
  }, [inputValue, applyCleaning]);

  return (
    <AutocompleteElement
      className={className}
      required={required}
      options={options}
      multi={multi}
      label={label}
      size={size}
      variant={variant}
      restricted={restricted}
      disabled={disabled}
      loading={loading}
      limit={limit}
      inputValue={inputValue}
      defaultValue={initial}
      noOptionsText={noOptionsText}
      error={error}
      helperText={helperText}
      onOpen={() => {
        if (!initialLoadOnOpen) return;
        if (disabledFetch || appliedValue) return;
        if (hasFetchedRef.current) return;

        hasFetchedRef.current = true;
        fetchOptions("", responseSize);
      }}
      onInputChange={(_, value, reason) => {
        if (reason === "selectOption") {
          return;
        }
        setInputValue(value);
        if (disabledFetch) return;
        if (value.trim() === "") {
          if (!isFirstRender.current) {
            fetchOptions("", 20);
          }
        }
      }}
      onChange={(_: any, value: Option | Option[] | null) => {
        skipNextFetch.current = true;
        const ids = extractValue(value, multi, valueKey || "id");
        updateFields(ids, value);

        onSelectOption?.(value);
        setInputValue("");
      }}
      sx={sx}
    />
  );
};

export default memo(AutocompleteDynamic);

function isInvalidExtraParams(
  extraParams: Record<string, any> | Record<string, any>[] | undefined,
) {
  if (!extraParams) return false;

  return Object.values(extraParams).some((v) => v === null || v === undefined);
}

function extractValue(value: any, multi: boolean, valueKey: string) {
  if (multi) {
    if (!Array.isArray(value) || value.length === 0) return null;

    return value.map((item) => {
      if (item?.id === "__NONE__") return "__NONE__";
      if (item && valueKey in item) return item[valueKey];
      return null;
    });
  }

  if (!value) return null;

  if (value.id === "__NONE__") return "__NONE__";

  if (valueKey in value) return value[valueKey];

  return null;
}
